import React from "react";
import { Box } from "@mui/material";
import { COLORS } from "../../../theme/landingTheme";

/*
 * IconBase
 * Generic base component implemented as an arrow function.
 * Renders a circular container for an icon with configurable size,
 * background gradient, color, border, shadow and custom styles.
 * Props:
 * - icon: React component to render (passed as `Icon`)
 * - gradient/fallbackGradient: background gradients
 * - size: numeric pixel size (default 64)
 * - color: icon color
 * - boxShadow, border: box styling
 * - radius: borderRadius (defaults to 50% for a circle)
 * - sx, iconSx: MUI sx overrides applied to container and icon
 */
const IconBase = ({
  icon: Icon,
  gradient,
  size = 64,
  color,
  boxShadow,
  border,
  fallbackGradient,
  radius = "50%",
  sx = {},
  iconSx = {},
}) => (
  <Box
    sx={{
      position: "relative",
      width: size,
      height: size,
      borderRadius: radius,
      background: gradient || fallbackGradient,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow,
      mb: 1,
      border,
      ...sx,
    }}
  >
    {Icon && <Icon sx={{ fontSize: size * 0.47, color, ...iconSx }} />}
  </Box>
);

/* Dark variant
 * Renders the icon inside a dark-themed circular container. Uses
 * a strong drop shadow and white icon color for contrast.
 */
export function IconDark({ icon, gradient, size = 64 }) {
  return (
    <IconBase
      icon={icon}
      gradient={gradient}
      size={size}
      color="#fff"
      boxShadow="0 12px 24px rgba(0,0,0,0.12)"
      fallbackGradient={COLORS.GRADIENTS.MAIN()}
    />
  );
}

/* Light variant
 * Renders the icon inside a light/tertiary background container.
 * This variant is intended to blend with light-themed sections and
 * reuses IconBase; it previously included IconBadge behavior.
 */
export function IconLight({ icon, size = 64 }) {
  return (
    <IconBase
      icon={icon}
      size={size}
      color={COLORS.BACKGROUND.PRIMARY}
      fallbackGradient={COLORS.ICON?.SECONDARY}
      // boxShadow="0 12px 24px rgba(0,0,0,0.12)"
    />
  );
}
